import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import TrialEnded from "@/pages/TrialEnded";
import Terms from "@/pages/Terms";
import RefundPolicy from "@/pages/RefundPolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ResetPassword from "@/pages/ResetPassword";
import OwnerRecovery from "@/pages/OwnerRecovery";
import Upgrade from "@/pages/Upgrade";
import { PaymentResult } from "@/pages/PaymentResult";
import KeepsakeViewer from "@/pages/KeepsakeViewer";
import AddMemory from "@/pages/AddMemory";
import DesignPreview from "@/pages/DesignPreview";
import EbookStudio from "@/pages/EbookStudio";
import MemoryInsightCheckout from "@/pages/MemoryInsightCheckout";
import YearlyCheckout from "@/pages/YearlyCheckout";
import RefundRequest from "@/pages/RefundRequest";
import PaymentHistory from "@/pages/PaymentHistory";
import ClaimGift from "@/pages/ClaimGift";
import GoLiveChecklist from "@/pages/GoLiveChecklist";
import ChangePassword from "@/pages/ChangePassword";
import GiftCheckout from "@/pages/GiftCheckout";
import GiftDone from "@/pages/GiftDone";
import GiftLifetime from "@/pages/GiftLifetime";
import RedeemGift from "@/pages/RedeemGift";
import AccountAccess from "@/pages/AccountAccess";
import { SiteFooter } from "@/components/Branding";
import { PageNav } from "@/components/PageNav";

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10 text-[#8A7F76]">Loading…</div>;
  if (!user) return <Navigate to={`/account?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PageNav />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Navigate to="/account" replace />} />
            <Route path="/account" element={<AccountAccess />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refunds" element={<RefundPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/owner-recovery" element={<OwnerRecovery />} />
            <Route path="/claim" element={<ClaimGift />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/payment/success" element={<PaymentResult mode="success" />} />
            <Route path="/payment/paymongo/success" element={<PaymentResult mode="success" provider="paymongo" />} />
            <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
            <Route path="/design-preview" element={<DesignPreview />} />
            <Route path="/ebook-studio-demo" element={<Navigate to="/ebook-studio" replace />} />
            <Route
              path="/ebook-studio"
              element={
                <ProtectedRoute>
                  <EbookStudio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memory-insight/:id"
              element={
                <ProtectedRoute>
                  <MemoryInsightCheckout />
                </ProtectedRoute>
              }
            />
            <Route path="/gift" element={<GiftLifetime />} />
            <Route path="/gift/checkout" element={<ProtectedRoute><GiftCheckout /></ProtectedRoute>} />
            <Route path="/gift/done" element={<ProtectedRoute><GiftDone /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
            <Route path="/admin/go-live" element={<ProtectedRoute><GoLiveChecklist /></ProtectedRoute>} />
            <Route path="/admin/password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/redeem" element={<ProtectedRoute><RedeemGift /></ProtectedRoute>} />
            <Route
              path="/refund-request"
              element={
                <ProtectedRoute>
                  <RefundRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/yearly-reflection/:year"
              element={
                <ProtectedRoute>
                  <YearlyCheckout />
                </ProtectedRoute>
              }
            />
            <Route path="/trial-ended" element={<TrialEnded />} />
            <Route
              path="/timeline"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-memory"
              element={
                <ProtectedRoute>
                  <AddMemory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/keepsake/:id"
              element={
                <ProtectedRoute>
                  <KeepsakeViewer />
                </ProtectedRoute>
              }
            />
          </Routes>
          <SiteFooter />
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </div>
  );
}

export default App;
